import {
  isSizeOption,
  parsePackSize,
} from '@/product/product-selection/pack-size';
import {ProductState, VariantState} from '@/product/types';
import {logError} from '@/utilities/log';

/**
 * Calculates the savings percentage for a given pack option and product.
 * The savings percentage is calculated by comparing the price of the cheapest
 * pack option to the price of the selected pack option.
 * If the pack option is not found, an error is logged and 0 is returned.
 *
 * @param packOption - The pack option to calculate the savings for.
 * @param product - The product state containing the variants.
 * @returns The savings percentage as a decimal number.
 */
export function getPackSavingsPercent(
  packOption: string,
  product: ProductState
): number {
  const packVariant = product.variants.find(
    (variant) => getPackOption(variant, product.sizeOptionNames) === packOption
  );

  if (!packVariant) {
    logError(new Error('Pack variant not found'), {
      message: 'ErrorCalculatingPackSavingsPercent',
      packOption,
      product,
    });

    return 0;
  }

  const compareAtPrice = getPackCompareAtPrice(packOption, product);
  return 1 - packVariant.price / compareAtPrice;
}

/**
 * Calculates the compare at price for a given pack option and product.
 * This is the price you would pay for this number of packs
 * if you bought the smallest pack option.
 * For example, buying quantity 3 of the 1 pack, vs buying a 3 pack.
 *
 * @param packOption - The pack option to calculate the compare at price for.
 * @param product - The product state object containing variants.
 * @returns The compare at price for the specified pack option.
 */
export function getPackCompareAtPrice(
  packOption: string,
  product: ProductState
) {
  const cheapestVariant = product.variants.reduce((lowest, variant) =>
    lowest.price < variant.price ? lowest : variant
  );
  const cheapestPackOption = getPackOption(
    cheapestVariant,
    product.sizeOptionNames
  );

  return (
    (cheapestVariant.price * parsePackSize(packOption)) /
    parseInt(cheapestPackOption)
  );
}

/**
 * Retrieves the pack option for a given variant.
 * This is the selected value for the 'Pack Size' option, or '1 Pack' if not found.
 *
 * @param variant - The variant state.
 * @returns The pack option for the variant.
 */
export function getPackOption(
  variant: VariantState,
  sizeOptionNames?: string[] | null
): string {
  return (
    variant.selectedOptions.find((option) =>
      isSizeOption(option, sizeOptionNames)
    )?.value ?? '1 Pack'
  );
}
